package dev.lingvo.site.components

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberUpdatedState
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.core.rememberPageContext
import dev.lingvo.site.ds.LKeyValueRow
import dev.lingvo.site.ds.LModal
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.nav.NavGlyph
import dev.lingvo.site.pages.app.projects.slug.screenshots.ScreenshotsAreaState
import dev.lingvo.site.pages.app.projects.slug.screenshots.ScreenshotsTab
import dev.lingvo.site.pages.app.projects.slug.screenshots.screenshotsAgentsPath
import dev.lingvo.site.pages.app.projects.slug.screenshots.screenshotsPath
import dev.lingvo.site.pages.app.projects.slug.screenshots.screenshotsTemplatesPath
import kotlinx.browser.document
import kotlinx.browser.window
import org.w3c.dom.Element
import org.w3c.dom.events.Event
import org.w3c.dom.events.KeyboardEvent

enum class CommandKind { DESTINATION, PROJECT, LOCALE, ACTION, KEY }

/**
 * One row in the palette.
 *
 * @param path null for an action, which runs rather than navigates.
 * @param hint Trailing context shown beside the label — the kind of thing this row is ("Project",
 *   "Language", "Settings") when the row's own label does not already say so, or an action's
 *   keyboard shortcut. Never the destination itself: [path] already carries that.
 * @param glyph the mark drawn at the head of the row. Destinations carry the nav model's own mark so
 *   the palette and the rail agree; projects draw their mark at the call site, which is why this can
 *   be null on a row that is still not blank.
 * @param onRun what an [CommandKind.ACTION] row does. A row with neither a [path] nor an [onRun] is
 *   inert and is never built — [commandPaletteItems] is what enforces that, because the alternative
 *   is a palette row that swallows a press.
 */
data class CommandItem(
    val id: String,
    val label: String,
    val kind: CommandKind,
    val path: String?,
    val hint: String? = null,
    val glyph: NavGlyph? = null,
    val onRun: (() -> Unit)? = null,
)

/**
 * Filter and order palette rows for a query.
 *
 * Prefix before contained, then the caller's order — which is significance, not alphabet, because
 * the caller knows that Overview matters more than Glossary and this function does not. Deliberately
 * not fuzzy: a fuzzy matcher that returns something for every query trains people to stop reading
 * the list, and the palette's whole value is that its first row is usually right.
 *
 * **[CommandKind.KEY] rows are never filtered.** They are the server's own answer to this exact
 * query — `GET /keys?search=` matches a key's *source text* as well as its name — so re-testing them
 * against the label here would throw away precisely the rows a text search exists to find: somebody
 * typing "checkout" to reach `cart.step_two`, whose name contains none of it.
 */
fun rankCommands(items: List<CommandItem>, query: String): List<CommandItem> {
    val needle = query.trim().lowercase()
    val available = (items + ScreenshotCommandRegistry.items).distinctBy { it.id }
    if (needle.isEmpty()) return available
    val prefix = mutableListOf<CommandItem>()
    val contained = mutableListOf<CommandItem>()
    val server = mutableListOf<CommandItem>()
    available.forEach { item ->
        val haystack = item.label.lowercase()
        when {
            item.kind == CommandKind.KEY -> server += item
            haystack.startsWith(needle) -> prefix += item
            haystack.contains(needle) -> contained += item
        }
    }
    return prefix + contained + server
}

/**
 * The order the palette's sections appear in, whatever order the rows arrived in.
 *
 * **Doing something outranks going somewhere.** The palette used to be a flat list of every
 * destination the rail already draws, with the one row that spends credits — when it existed at all
 * — buried among forty links. Actions first, then the strings a query is most likely to have meant,
 * then the places.
 */
private val COMMAND_KIND_ORDER = listOf(
    CommandKind.ACTION,
    CommandKind.KEY,
    CommandKind.PROJECT,
    CommandKind.LOCALE,
    CommandKind.DESTINATION,
)

/**
 * Ranked rows, cut into the sections the palette draws a divider between.
 *
 * Rank order is preserved *within* a section, so the best match for a query is still the first row
 * of its kind — grouping decides which heading a row sits under, never how well it matched. An empty
 * section is dropped rather than drawn as a heading with nothing beneath it.
 */
fun groupCommands(ranked: List<CommandItem>): List<Pair<CommandKind, List<CommandItem>>> =
    COMMAND_KIND_ORDER.mapNotNull { kind ->
        ranked.filter { it.kind == kind }.takeIf(List<CommandItem>::isNotEmpty)?.let { kind to it }
    }

/** Mounted area actions supplement the shared palette without leaking callbacks after navigation. */
private object ScreenshotCommandRegistry {
    var owner: Any? = null
    var items: List<CommandItem> by mutableStateOf(emptyList())
}

/** Registers real area actions with route-local upload and shortcut handlers. */
@Composable
fun registerScreenshotCommands(
    slug: String, params: ScreenshotsAreaState,
    canUpload: Boolean, onUpload: () -> Unit,
) {
    val ctx = rememberPageContext()
    val upload = rememberUpdatedState(onUpload)
    var shortcuts by remember { mutableStateOf(false) }
    DisposableEffect(slug, params, canUpload) {
        val items = mutableListOf<CommandItem>()
        fun action(id: String, label: String, glyph: NavGlyph, run: () -> Unit) {
            items += CommandItem("screenshots-$id", label, CommandKind.ACTION, null, glyph = glyph, onRun = run)
        }
        if (canUpload) action("compose", tr("command_palette_model.compose_the_set", "Compose the set"), NavGlyph.Smartphone) {
            ctx.router.tryRoutingTo(screenshotsPath(slug,
                ScreenshotsTab.REVIEW, params))
        }
        action("retakes", tr("command_palette_model.open_the_retake_queue", "Open the retake queue"), NavGlyph.Bot) {
            ctx.router.tryRoutingTo(screenshotsAgentsPath(slug, params))
        }
        if (canUpload) action("upload", tr("command_palette_model.upload_captures", "Upload captures"), NavGlyph.Files) { upload.value() }
        action("templates", tr("command_palette_model.open_templates", "Open templates"), NavGlyph.Grid) {
            ctx.router.tryRoutingTo(screenshotsTemplatesPath(slug, params))
        }
        action("shortcuts", tr("command_palette_model.show_keyboard_shortcuts", "Show keyboard shortcuts"), NavGlyph.Help) { shortcuts = true }
        val owner = Any(); ScreenshotCommandRegistry.owner = owner
        ScreenshotCommandRegistry.items = items
        val listener: (Event) -> Unit = { event ->
            val key = event as? KeyboardEvent
            val tag = (key?.target as? Element)?.tagName
            if (key?.key == "?" && key.asDynamic().isComposing != true && !key.ctrlKey && !key.metaKey && !key.altKey && tag !in setOf("INPUT", "TEXTAREA", "SELECT") &&
                (key.target as? Element)?.closest("[contenteditable=true]") == null &&
                document.querySelector("[role=dialog]") == null) {
                key.preventDefault(); shortcuts = true
            }
        }
        window.addEventListener("keydown", listener)
        onDispose { if (ScreenshotCommandRegistry.owner === owner) { ScreenshotCommandRegistry.owner = null; ScreenshotCommandRegistry.items = emptyList() }; window.removeEventListener("keydown", listener) }
    }
    LModal(shortcuts, t("command_palette_model.screenshot_keyboard_shortcuts", "Screenshot keyboard shortcuts"), { shortcuts = false }) {
        LKeyValueRow(t("command_palette_model.move_through_coverage", "Move through coverage"), "↑ ↓ ← → · Home · End · Ctrl+Home · Ctrl+End") // i18n-ignore
        LKeyValueRow(t("command_palette_model.open_capture", "Open capture"), "Enter · Space") // i18n-ignore
        LKeyValueRow(t("command_palette_model.reorder_a_shot", "Reorder a shot"), "Space · ↑ ↓ · Space · Esc · Alt+↑ ↓") // i18n-ignore
    }
}
